import { useId } from "react";
import { BadgeConfig } from "../data/clubBadges";

interface Props {
  config: BadgeConfig;
  size?: number;
  title?: string;
}

/**
 * Renders a simplified heraldic shield badge as an SVG.
 * viewBox: 0 0 40 46
 * Shield path: M 20 1 L 39 8 L 39 27 Q 39 40 20 45 Q 1 40 1 27 L 1 8 Z
 */
export default function ClubBadge({ config, size = 36, title }: Props) {
  const uid = useId();
  const clipId = `shield-clip-${uid}`;

  const { primary, secondary, tertiary, pattern, flagUrl } = config;

  const shieldPath = "M 20 1 L 39 8 L 39 27 Q 39 40 20 45 Q 1 40 1 27 L 1 8 Z";

  const renderPattern = () => {
    switch (pattern) {
      case "solid":
        return <rect x="0" y="0" width="40" height="46" fill={primary} />;

      case "v_halves":
        // Left half primary, right half secondary
        return (
          <>
            <rect x="0" y="0" width="20" height="46" fill={primary} />
            <rect x="20" y="0" width="20" height="46" fill={secondary} />
          </>
        );

      case "h_halves":
        // Top half primary, bottom half secondary
        return (
          <>
            <rect x="0" y="0" width="40" height="23" fill={primary} />
            <rect x="0" y="23" width="40" height="23" fill={secondary} />
          </>
        );

      case "v_stripes": {
        // 4 vertical stripes alternating primary/secondary
        const stripeW = 10;
        return (
          <>
            {[0, 1, 2, 3].map((i) => (
              <rect key={i} x={i * stripeW} y="0" width={stripeW} height="46" fill={i % 2 === 0 ? primary : secondary} />
            ))}
          </>
        );
      }

      case "thin_v_stripes": {
        // 6 thin vertical stripes
        const stripeW = 40 / 6;
        return (
          <>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <rect key={i} x={i * stripeW} y="0" width={stripeW} height="46" fill={i % 2 === 0 ? primary : secondary} />
            ))}
          </>
        );
      }

      case "h_stripes": {
        // 4 horizontal stripes alternating primary/secondary
        const stripeH = 46 / 4;
        return (
          <>
            {[0, 1, 2, 3].map((i) => (
              <rect key={i} x="0" y={i * stripeH} width="40" height={stripeH} fill={i % 2 === 0 ? primary : secondary} />
            ))}
          </>
        );
      }

      case "sash":
        // Diagonal sash: primary bg, secondary diagonal band from top-left to bottom-right
        return (
          <>
            <rect x="0" y="0" width="40" height="46" fill={primary} />
            <polygon points="5,0 35,0 40,5 10,46 0,46 0,41" fill={secondary} opacity="0.9" />
          </>
        );

      case "v_thirds": {
        // Three vertical thirds
        const third = 40 / 3;
        const colors = [primary, secondary, tertiary ?? primary];
        return (
          <>
            {colors.map((c, i) => (
              <rect key={i} x={i * third} y="0" width={third} height="46" fill={c} />
            ))}
          </>
        );
      }

      case "h_thirds": {
        // Three horizontal thirds
        const thirdH = 46 / 3;
        const colors = [primary, secondary, tertiary ?? primary];
        return (
          <>
            {colors.map((c, i) => (
              <rect key={i} x="0" y={i * thirdH} width="40" height={thirdH} fill={c} />
            ))}
          </>
        );
      }

      case "quarters":
        // Four quarters: TL=primary, TR=secondary, BL=secondary, BR=primary
        return (
          <>
            <rect x="0" y="0" width="20" height="23" fill={primary} />
            <rect x="20" y="0" width="20" height="23" fill={secondary} />
            <rect x="0" y="23" width="20" height="23" fill={secondary} />
            <rect x="20" y="23" width="20" height="23" fill={primary} />
          </>
        );

      default:
        return <rect x="0" y="0" width="40" height="46" fill={primary} />;
    }
  };

  return (
    <svg
      width={size}
      height={Math.round(size * 46 / 40)}
      viewBox="0 0 40 46"
      style={{ display: "block", flexShrink: 0 }}
      aria-label={title}
      role={title ? "img" : "presentation"}
    >
      <defs>
        <clipPath id={clipId}>
          <path d={shieldPath} />
        </clipPath>
      </defs>

      {/* Pattern fill (clipped to shield) */}
      <g clipPath={`url(#${clipId})`}>
        {flagUrl ? (
          // Flags from the CDN are 4:3 (landscape). The shield viewBox is 40×46
          // (portrait), so naïve slice/cover is unreliable across browsers.
          // Instead, pre-calculate: scale the flag to fill the shield's full
          // height (46 units) → width = 46 * (4/3) ≈ 61.3 units, then centre
          // horizontally so the overflow is split equally left and right.
          // The clipPath trims everything outside the shield outline.
          <image
            href={flagUrl}
            x="-10.7"
            y="0"
            width="61.3"
            height="46"
            preserveAspectRatio="xMidYMid meet"
          />
        ) : (
          renderPattern()
        )}
      </g>

      {/* Shield outline — light rim so the badge reads against dark surfaces */}
      <path
        d={shieldPath}
        fill="none"
        stroke="rgba(255,255,255,0.22)"
        strokeWidth="1.5"
      />
    </svg>
  );
}
